import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Banner, Button, Field } from "../ui/Field";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const emailFromUrl = params.get("email") ?? "";
  const [email, setEmail] = useState(emailFromUrl);
  const [deviceLabel, setDeviceLabel] = useState(defaultDeviceLabel());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) setErr("Missing invite token. Check the URL.");
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const options = await api.post<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
        "/api/auth/register/options",
        { email, invite_token: token },
      );
      const attestation = await startRegistration({ optionsJSON: options });
      await api.post("/api/auth/register/verify", { email, attestation, device_label: deviceLabel });
      setOk(true);
      await refresh();
      setTimeout(() => nav("/sites"), 800);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--color-paper)] bg-paper-noise">
      <div className="w-full max-w-md reveal">
        <div className="text-center mb-8">
          <div className="inline-flex items-baseline gap-1.5 mb-6">
            <span className="display text-3xl leading-none">CMS</span>
            <span className="text-[var(--color-accent)] font-mono text-[18px]">+</span>
          </div>
          <div className="section-no mb-2">№ Invite</div>
          <h1 className="display text-4xl mb-3">You're in.</h1>
          <p className="text-[13.5px] text-[var(--color-muted)] max-w-sm mx-auto leading-relaxed">
            Register a passkey on this device. It replaces passwords entirely — phishing-proof and biometric-locked.
          </p>
        </div>

        {err && <Banner kind="error">{err}</Banner>}
        {ok && <Banner kind="success" title="Passkey registered.">Redirecting to your workspace…</Banner>}

        <form onSubmit={onSubmit}>
          <Field
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label="Device label"
            value={deviceLabel}
            onChange={(e) => setDeviceLabel(e.target.value)}
            help="Shown in your account later so you can recognise this passkey."
          />
          <Button type="submit" disabled={busy || !token} size="lg" className="w-full mt-2">
            {busy ? "Registering…" : "Create passkey →"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function defaultDeviceLabel() {
  const ua = navigator.userAgent;
  if (/Macintosh/.test(ua)) return "Mac";
  if (/iPhone|iPad/.test(ua)) return "iPhone/iPad";
  if (/Windows/.test(ua)) return "Windows";
  if (/Android/.test(ua)) return "Android";
  if (/Linux/.test(ua)) return "Linux";
  return "This device";
}
